import {
  ListDocumentDeliveriesForAccountApiArg,
  useListDocumentDeliveriesForAccountQuery,
} from "@generated";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { DocumentDelivery } from "../types";

export interface HookArgs {
  page?: ListDocumentDeliveriesForAccountApiArg["page"];
  filter?: ListDocumentDeliveriesForAccountApiArg["filter"];
}

const formatRecipients = (recipients: unknown): string => {
  if (!recipients || typeof recipients !== "object") return "";
  const values = Object.values(recipients as Record<string, unknown>).filter(
    (v): v is string => typeof v === "string" && v.length > 0
  );
  return values.join(", ");
};

const useListDocumentDeliveries = ({ page, filter }: HookArgs) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);

  const { data, error, isLoading, isFetching, refetch } =
    useListDocumentDeliveriesForAccountQuery({
      slug: accountSlug,
      include: ["project"],
      page,
      filter,
    });

  let deliveries: DocumentDelivery[] = [];
  let customError: string | null = null;
  let pageMeta = { recordCount: 0, pageCount: 0 };

  if (data?.data) {
    try {
      const projectsById = new Map<string, string>();
      for (const item of data.included ?? []) {
        if (item.type === "projects") {
          projectsById.set(
            String(item.id),
            (item.attributes?.["project-name"] as string) ?? ""
          );
        }
      }

      deliveries = data.data
        .filter((d) => !!d.id)
        .map((delivery) => {
          const projectRel = (
            delivery.relationships as Record<string, { data?: unknown }>
          )?.project?.data as { id?: number | string } | undefined;
          const projectId = projectRel?.id ? String(projectRel.id) : null;

          const attrs = delivery.attributes as Record<string, unknown>;
          const status =
            (attrs.status as DocumentDelivery["status"]) ?? "pending";

          return {
            id: String(delivery.id),
            projectName: projectId ? projectsById.get(projectId) ?? "" : "",
            documentName: (attrs["document-name"] as string) ?? "",
            deliveryType: (attrs.delivery_type as string) ?? "",
            recipients: formatRecipients(attrs.recipients),
            status,
            errorInfo:
              status === "failed" ? (attrs.error_info as string) ?? null : null,
          };
        });

      pageMeta = {
        recordCount: data.meta?.["record-count"] ?? 0,
        pageCount: data.meta?.["page-count"] ?? 0,
      };
    } catch (err) {
      customError =
        err instanceof Error ? err.message : "An unknown error occurred";
    }
  }

  return {
    deliveries,
    pageMeta,
    isLoading,
    isFetching,
    error: error ?? customError,
    refetch,
  };
};

export default useListDocumentDeliveries;
